import React, { useContext, useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import { useEvent } from '@js/common/hooks/useEvent'
import { UA } from '@js/common/UA'

import {
  QuizContext,
  QuizContextType
} from '@f/applications/QuizApp/context/QuizContext'
import { QuizStep } from '@f/applications/QuizApp/partials/types'
import { QuestionLoanDetailsSchema } from '@f/applications/QuizApp/questions/QuestionLoanDetails/types'
import { Form } from '@f/elements/HookForm'
import yup from '@f/elements/HookForm/validator/yup'

import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

import { budgetRanges } from '../../partials/utils'

const calculateBudgetRange = (budgetRange: { min: number; max: number }) => {
  if (budgetRange.max === Infinity) {
    return 630_000
  }
  return (budgetRange.min + budgetRange.max) / 2
}

interface QuestionLoanDetailsProps {
  step: QuizStep
}

const getStringFromFloat = (value?: number) =>
  value ? value.toString().replace('.', ',') : ''

const sharedValidations = {
  equity: yup
    .number()
    .transform((_value, originalValue) =>
      originalValue
        ? Number(originalValue.replace(/\./, '').replace(/,/, '.'))
        : originalValue
    )
    .min(0, 'Bitte geben Sie einen gültigen Wert ein.')
    .typeError('Bitte geben Sie einen gültigen Wert ein.')
    .required('Bitte geben Sie einen Wert ein.'),
  agent_fee_present: yup
    .mixed()
    .oneOf(['with-agent-fee', 'without-agent-fee'])
    .required('Bitte wählen Sie eine Antwort aus.'),

  agent_fee: yup.mixed().when('agent_fee_present', {
    is: (agentFeePresent: string) => agentFeePresent === 'with-agent-fee',
    then: () =>
      yup
        .number()
        .transform((_value, originalValue) =>
          originalValue
            ? Number(originalValue.replace(/\./, '').replace(/,/, '.'))
            : originalValue
        )
        .min(0, 'Bitte geben Sie einen gültigen Wert ein.')
        .max(100, 'Bitte geben Sie einen gültigen Wert ein.')
        .typeError('Bitte geben Sie einen gültigen Wert ein.')
        .required('Bitte geben Sie einen Wert ein.')
  })
}

const formatPercentageValueToFloat = (value: string): number =>
  (Number(value) * 100) / 10_000

const formatFloatValueToPercentage = (value: string): string =>
  (Number(value) * 10_000) / 100 + ''

const buildingLoanDetailsValidationSchema = yup.object().shape({
  building_costs: yup
    .number()
    .transform((_value, originalValue) =>
      originalValue
        ? Number(originalValue.replace(/\./, '').replace(/,/, '.'))
        : originalValue
    )
    .min(0, 'Bitte geben Sie einen gültigen Wert ein.')
    .typeError('Bitte geben Sie einen gültigen Wert ein.')
    .required('Bitte geben Sie einen Wert ein.'),
  property_costs: yup
    .number()
    .transform((_value, originalValue) =>
      originalValue
        ? Number(originalValue.replace(/\./, '').replace(/,/, '.'))
        : originalValue
    )
    .min(0, 'Bitte geben Sie einen gültigen Wert ein.')
    .typeError('Bitte geben Sie einen gültigen Wert ein.')
    .required('Bitte geben Sie einen Wert ein.'),
  ...sharedValidations
})

const otherPurposeLoanDetailsValidationSchema = yup.object().shape({
  purchase_price: yup
    .number()
    .transform((_value, originalValue) =>
      originalValue
        ? Number(originalValue.replace(/\./, '').replace(/,/, '.'))
        : originalValue
    )
    .min(0, 'Bitte geben Sie einen gültigen Wert ein.')
    .typeError('Bitte geben Sie einen gültigen Wert ein.')
    .required('Bitte geben Sie einen Wert ein.'),
  modernization_costs: yup
    .number()
    .transform((_value, originalValue) =>
      originalValue
        ? Number(originalValue.replace(/\./, '').replace(/,/, '.'))
        : originalValue
    )
    .min(0, 'Bitte geben Sie einen gültigen Wert ein.')
    .typeError('Bitte geben Sie einen gültigen Wert ein.')
    .required('Bitte geben Sie einen Wert ein.'),
  ...sharedValidations
})

const getDefaultValuesForHouseQuizUser = (
  budgetRange: typeof budgetRanges[0]
) => {
  const buildingCost = calculateBudgetRange(budgetRange)
  const propertyCost = 120_000
  const DEFAULT_AGENT_FEE = 0.0357

  return {
    building_costs: getStringFromFloat(buildingCost),
    property_costs: getStringFromFloat(propertyCost),
    equity: getStringFromFloat((buildingCost + propertyCost) * 0.1),
    agent_fee_present: 'with-agent-fee',
    agent_fee: getStringFromFloat(
      Number(formatFloatValueToPercentage(DEFAULT_AGENT_FEE.toString()))
    )
  }
}

export const QuestionLoanDetails = ({ step }: QuestionLoanDetailsProps) => {
  const {
    actions: { updateValues, move },
    data: { steps }
  } = useContext(QuizContext) as $TSFixMe
  const userData = UA.getLS()
  const budgetRange = budgetRanges.find(
    range => range.range === userData?.budget_range
  )
  const isUserComingFromHouseQuiz = budgetRange !== undefined
  const defaultValuesForHouseQuizUser = isUserComingFromHouseQuiz
    ? getDefaultValuesForHouseQuizUser(budgetRange)
    : {}

  const formRef = useRef<HTMLFormElement>(null)

  const loanPurpose = steps.find((s: QuizStep) => s.id === 'loan_purpose')
    ?.values?.[0]?.value
  const loanPurposeIsBuilding =
    loanPurpose === 'building_project' || loanPurpose === undefined

  const currentValues = steps
    .find((s: QuizStep) => s.id === 'loan_details')
    ?.values?.reduce(
      (acc, current) =>
        current.value
          ? {
              ...acc,
              [current.name]: current.value
            }
          : acc,
      {}
    ) as QuestionLoanDetailsSchema

  const questionLoanDetailsValidationSchema: yup.ObjectSchema<QuestionLoanDetailsSchema> = loanPurposeIsBuilding
    ? buildingLoanDetailsValidationSchema
    : otherPurposeLoanDetailsValidationSchema

  const defaultValues: Partial<QuestionLoanDetailsSchema> = loanPurposeIsBuilding
    ? {
        building_costs: getStringFromFloat(currentValues?.building_costs),
        property_costs: getStringFromFloat(currentValues?.property_costs),
        equity: getStringFromFloat(currentValues?.equity),
        agent_fee_present:
          currentValues?.agent_fee >= 0
            ? currentValues.agent_fee === 0
              ? 'without-agent-fee'
              : 'with-agent-fee'
            : undefined,
        agent_fee: getStringFromFloat(
          Number(formatFloatValueToPercentage(currentValues?.agent_fee))
        ),
        ...defaultValuesForHouseQuizUser
      }
    : {
        purchase_price: getStringFromFloat(currentValues?.purchase_price),
        modernization_costs: getStringFromFloat(
          currentValues?.modernization_costs
        ),
        equity: getStringFromFloat(currentValues?.equity),
        agent_fee_present:
          currentValues?.agent_fee >= 0
            ? currentValues.agent_fee === 0
              ? 'without-agent-fee'
              : 'with-agent-fee'
            : undefined,
        agent_fee: getStringFromFloat(
          Number(formatFloatValueToPercentage(currentValues?.agent_fee))
        )
      }

  const handleSubmit = (values: QuestionLoanDetailsSchema) => {
    const hasAgentFee = values.agent_fee_present === 'with-agent-fee'
    const valuesToSave = loanPurposeIsBuilding
      ? [
          {
            name: 'building_costs',
            value: values.building_costs
          },
          {
            name: 'property_costs',
            value: values.property_costs
          },
          {
            name: 'equity',
            value: values.equity
          },
          {
            name: 'agent_fee',
            value: hasAgentFee
              ? formatPercentageValueToFloat(values.agent_fee)
              : 0
          }
        ]
      : [
          {
            name: 'purchase_price',
            value: values.purchase_price
          },
          {
            name: 'modernization_costs',
            value: values.modernization_costs
          },
          {
            name: 'equity',
            value: values.equity
          },
          {
            name: 'agent_fee',
            value: hasAgentFee
              ? formatPercentageValueToFloat(values.agent_fee)
              : 0
          }
        ]
    updateValues({
      stepID: step.id,
      valuesToSave
    })

    move({ to: 'next' })
  }

  useEvent('NEXT_QUESTION_BUTTON_CLICKED', () => {
    formRef?.current?.dispatchEvent(
      new Event('submit', { cancelable: true, bubbles: true })
    )
  })

  return (
    <Form.Wrapper<QuestionLoanDetailsSchema>
      ref={formRef}
      className='[ f-m-t-020 ]'
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      mode='all'
      wtEnabled={false}
      validationSchema={questionLoanDetailsValidationSchema}>
      <div className='f-d-flc f-fl-al-fs f-fl-wrap f-m-h-auto f-wrap QuestionLoanDetails'>
        <div className='f-t-tmedium f-m-b-020 f-t-left'>{step.heading}</div>
        <div className='f-p-h-020:m f-p-h-020:l'>
          <Form.Text
            className='[ f-m-b-020 f-c-bo-neu050 ] has-border is-full-width'
            name={loanPurposeIsBuilding ? 'building_costs' : 'purchase_price'}
            placeholder={
              loanPurposeIsBuilding ? 'Baukosten (€)' : 'Kaufpreis (€)'
            }
          />
          <Form.Text
            className='[ f-m-b-020 f-c-bo-neu050 ] has-border is-full-width'
            name={
              loanPurposeIsBuilding ? 'property_costs' : 'modernization_costs'
            }
            placeholder={
              loanPurposeIsBuilding
                ? 'Grundstückskosten (€)'
                : 'Modernisierungskosten (€)'
            }
          />
          <Form.Text
            className='[ f-m-b-020 f-c-bo-neu050 ] has-border is-full-width'
            name='equity'
            placeholder='Eigenkapital (€)'
          />
          <AgentFeeRadio step={step} />
          <div className='f-t-mini f-m-t-020'>
            Wenn Sie noch keine Immobilie gefunden haben, geben Sie einfach den
            Kaufpreis ein, den Sie für Ihre Immobilie planen.
          </div>
        </div>
      </div>
    </Form.Wrapper>
  )
}

const AgentFeeRadio = ({ step }: { step: QuizStep }) => {
  const context = useContext(QuizContext) as QuizContextType
  const { watch } = useFormContext()

  const agentFeeStatus = watch('agent_fee_present')

  const agentFeeInputVisible = agentFeeStatus === 'with-agent-fee'

  return (
    <>
      <Form.Radio
        radioClassName='[ f-m-b-020 f-c-ba-whi f-c-fo-pri f-c-bo-neu050 f-m-r-040 ]'
        radioLabelClassName='f-m-l-010 f-t-mini'
        radioErrorFullWidth
        radioErrorMessageClassName='f-m-b-005'
        className='f-d-flr'
        radioTheme='basic'
        name='agent_fee_present'
        radioOptions={[
          { value: 'with-agent-fee', label: 'Mit Maklergebühr' },
          { value: 'without-agent-fee', label: 'Ohne Maklergebühr' }
        ]}
        wtData={{
          radiobutton: {
            location: `question-${step._wtName}`,
            ...context.tracking.flow
          }
        }}
      />
      {agentFeeInputVisible && (
        <Form.Text
          className='[ f-m-b-040 f-c-bo-neu050 ] has-border is-full-width'
          name='agent_fee'
          placeholder='Maklergebühr (%)'
        />
      )}
    </>
  )
}
